import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import DiskUsageBanner from "@/components/DiskUsageBanner";
import ActivityPill from "@/components/ActivityPill";
import {
  Camera, ArrowLeft, LogOut, Users, ShieldCheck, ChevronDown,
} from "lucide-react";

/**
 * Shared top nav used across JobsPage, JobDetailPage, and JobReviewPage.
 *
 * Layout:
 *   [logo] [breadcrumb / page title] ......... [page actions] [user menu]
 *
 * Admin-only items (Users, Audit log) live inside a user-avatar dropdown
 * to keep the top bar visually clean instead of laying out four+ buttons
 * side-by-side.
 */
export default function AppTopBar({
  backTo,
  eyebrow,
  title,
  titleAdornment,
  actions,
}) {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const initial = (user?.email || "?").trim().charAt(0).toUpperCase();

  return (
    <header
      className="border-b border-border bg-white/95 backdrop-blur sticky top-0 z-30"
      data-testid="app-top-bar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {backTo !== undefined && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => nav(backTo)}
              className="h-9 w-9 hover:bg-slate-100 rounded-md shrink-0"
              data-testid="btn-back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <button
            type="button"
            onClick={() => nav("/")}
            title="PhotoPick Studio — go to jobs"
            aria-label="Go to home"
            className="w-8 h-8 rounded-md bg-primary flex items-center justify-center shrink-0 hover:bg-primary/90 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
            data-testid="topbar-logo-home"
          >
            <Camera className="h-4 w-4 text-primary-foreground" />
          </button>
          <div className="min-w-0 flex items-center gap-3">
            {title ? (
              <div className="min-w-0">
                {eyebrow && (
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 leading-none">
                    {eyebrow}
                  </p>
                )}
                <h1
                  className="font-heading font-bold text-lg tracking-tight truncate leading-tight"
                  data-testid="topbar-title"
                >
                  {title}
                </h1>
              </div>
            ) : (
              <span className="font-heading font-bold tracking-tight">
                PhotoPick&nbsp;Studio
              </span>
            )}
            {titleAdornment}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {actions}

          <ActivityPill />
          <DiskUsageBanner />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-2 pl-1.5 pr-2 h-9 rounded-full border border-border hover:bg-slate-50 transition-colors"
                data-testid="user-menu-trigger"
              >
                <span
                  className="w-7 h-7 rounded-full bg-slate-900 text-white text-[11px] font-bold flex items-center justify-center"
                  aria-hidden
                >
                  {initial}
                </span>
                <span
                  className="hidden md:inline text-[13px] font-semibold text-slate-700 truncate max-w-[160px]"
                  data-testid="user-email"
                >
                  {user?.email}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
              <DropdownMenuLabel className="pb-1">
                <p className="text-[13px] font-semibold text-slate-900 truncate">
                  {user?.email}
                </p>
                <p
                  className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mt-0.5"
                  data-testid="user-role-badge"
                >
                  {isAdmin ? "Admin" : "Editor"}
                </p>
              </DropdownMenuLabel>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => nav("/admin/users")}
                    data-testid="nav-users"
                  >
                    <Users className="h-4 w-4" />
                    Users
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => nav("/admin/audit")}
                    data-testid="nav-audit"
                  >
                    <ShieldCheck className="h-4 w-4" />
                    Audit log
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={logout}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                data-testid="nav-logout"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
